import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trash2, Plus } from "lucide-react";
import { colors, type, spacing, radius } from "@/theme";
import { api } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
function euro(cents) { return (cents / 100).toFixed(2).replace(".", ","); }
function toCents(s) { const n = parseFloat((s || "").replace(",", ".")); return isNaN(n) ? 0 : Math.round(n * 100); }

function Switch({ value, onChange, testID, on = colors.accent }) {
  return (
    <button data-testid={testID} onClick={() => onChange(!value)} role="switch" aria-checked={value} style={{ width: 48, height: 28, borderRadius: 14, border: "none", background: value ? on : colors.lineStrong, position: "relative", padding: 0, flexShrink: 0 }}>
      <span style={{ position: "absolute", top: 3, left: value ? 23 : 3, width: 22, height: 22, borderRadius: 11, background: "#fff", transition: "left .18s ease" }} />
    </button>
  );
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const { user, logout } = useRequireAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [f, setF] = useState(null);
  const [copertoStr, setCopertoStr] = useState("0,00");
  const [orari, setOrari] = useState([]);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const d = await api.getSettings();
      setF(d);
      setCopertoStr(euro(d.coperto_cent || 0));
      const existing = d.orari_apertura || [];
      const built = DAYS.map((_, i) => {
        const g = i + 1;
        const e = existing.find((x) => x.giorno === g);
        return e ? { giorno: g, chiuso: !!e.chiuso, fasce: e.fasce?.length ? e.fasce : [{ da: "12:00", a: "15:00" }] }
                 : { giorno: g, chiuso: false, fasce: [{ da: "12:00", a: "15:00" }, { da: "19:00", a: "23:00" }] };
      });
      setOrari(built);
      setLoading(false);
    })();
  }, [user]);

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      const cleanOrari = orari.map((d) => ({ giorno: d.giorno, chiuso: d.chiuso, fasce: d.chiuso ? [] : d.fasce.filter((x) => x.da && x.a) }));
      const res = await api.putSettings({ nome: f.nome, indirizzo: f.indirizzo, telefono: f.telefono, colore_accento: f.colore_accento, coperto_cent: toCents(copertoStr), nota_contaminazione: f.nota_contaminazione, note_rapide: f.note_rapide, scadenza_sessione_min: parseInt(String(f.scadenza_sessione_min)) || 120, orari_apertura: cleanOrari, flags: f.flags, privacy: f.privacy });
      setF(res); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setDelBusy(true); setDelErr(null);
    try { await api.deleteAccount(delPw); await logout(); navigate("/", { replace: true }); }
    catch (e) { setDelErr(e.message || "Errore"); } finally { setDelBusy(false); }
  };

  const setFlag = (k, v) => setF((p) => ({ ...p, flags: { ...p.flags, [k]: v } }));
  const setFascia = (di, fi, key, val) => setOrari((p) => p.map((d, i) => i === di ? { ...d, fasce: d.fasce.map((x, j) => j === fi ? { ...x, [key]: val } : x) } : d));
  const toggleFascia2 = (di) => setOrari((p) => p.map((d, i) => i === di ? { ...d, fasce: d.fasce.length > 1 ? [d.fasce[0]] : [...d.fasce, { da: "19:00", a: "23:00" }] } : d));

  if (loading) return <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", alignItems: "center", justifyContent: "center", ...type.sans, color: colors.accent }}>Caricamento…</div>;

  const rapide = f.note_rapide || [];

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px` }}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={plain}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.serif, fontSize: 22, color: colors.ink }}>Impostazioni</div>
          <button data-testid="save-settings-button" onClick={save} disabled={saving} style={{ background: "none", border: "none", ...type.sansSemi, fontSize: 16, color: colors.accent }}>{saving ? "…" : saved ? "Salvato ✓" : "Salva"}</button>
        </div>

        <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.lg }}>
          <Section title="Dati locale">
            <Field label="Nome" value={f.nome} onChange={(v) => setF({ ...f, nome: v })} testID="set-nome" />
            <Field label="Indirizzo" value={f.indirizzo} onChange={(v) => setF({ ...f, indirizzo: v })} testID="set-indirizzo" />
            <Field label="Telefono" value={f.telefono} onChange={(v) => setF({ ...f, telefono: v })} testID="set-telefono" />
            <Field label="Colore accento (hex)" value={f.colore_accento} onChange={(v) => setF({ ...f, colore_accento: v })} testID="set-colore" />
          </Section>

          <Section title="Coperto">
            <Field label="Importo a persona (€)" value={copertoStr} onChange={setCopertoStr} testID="set-coperto" />
            <Row label="Mostra coperto al cliente"><Switch testID="flag-mostra-coperto" value={!!f.flags?.mostra_coperto} onChange={(v) => setFlag("mostra_coperto", v)} /></Row>
          </Section>

          <Section title="Nota contaminazione (obbligo di legge)">
            <textarea data-testid="set-contaminazione" style={area} value={f.nota_contaminazione || ""} onChange={(e) => setF({ ...f, nota_contaminazione: e.target.value })} />
          </Section>

          <Section title="Informativa privacy (mostrata al cliente)">
            <textarea data-testid="set-privacy" style={{ ...area, minHeight: 140 }} value={f.privacy || ""} onChange={(e) => setF({ ...f, privacy: e.target.value })} />
          </Section>

          <Section title="Note rapide (max 6)">
            {rapide.map((n, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                <input data-testid={`rapida-${i}`} style={{ ...input, flex: 1 }} value={n} onChange={(e) => setF({ ...f, note_rapide: rapide.map((x, j) => j === i ? e.target.value : x) })} />
                <button data-testid={`rapida-del-${i}`} onClick={() => setF({ ...f, note_rapide: rapide.filter((_, j) => j !== i) })} style={plain}><Trash2 size={18} color={colors.danger} /></button>
              </div>
            ))}
            {rapide.length < 6 ? <button data-testid="rapida-add" onClick={() => setF({ ...f, note_rapide: [...rapide, ""] })} style={{ display: "flex", alignItems: "center", gap: spacing.xs, alignSelf: "flex-start", background: "none", border: "none" }}><Plus size={16} color={colors.accent} /><span style={{ ...type.sansSemi, fontSize: 14, color: colors.accent }}>Aggiungi nota</span></button> : null}
          </Section>

          <Section title="Orari di apertura">
            {orari.map((d, di) => (
              <div key={d.giorno} style={{ display: "flex", flexDirection: "column", gap: spacing.sm, borderBottom: `1px solid ${colors.line}`, paddingBottom: spacing.sm }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ ...type.sansSemi, fontSize: 15, color: colors.ink }}>{DAYS[di]}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                    <span style={{ ...type.sans, fontSize: 13, color: colors.inkSoft }}>{d.chiuso ? "Chiuso" : "Aperto"}</span>
                    <Switch testID={`chiuso-${d.giorno}`} value={!d.chiuso} on={colors.success} onChange={(v) => setOrari((p) => p.map((x, i) => i === di ? { ...x, chiuso: !v } : x))} />
                  </div>
                </div>
                {!d.chiuso ? (
                  <>
                    {d.fasce.map((fa, fi) => (
                      <div key={fi} style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
                        <input data-testid={`fascia-${d.giorno}-${fi}-da`} style={{ ...input, flex: 1, textAlign: "center", height: 44 }} value={fa.da} onChange={(e) => setFascia(di, fi, "da", e.target.value)} placeholder="12:00" />
                        <span style={{ ...type.sans, color: colors.inkSoft }}>–</span>
                        <input data-testid={`fascia-${d.giorno}-${fi}-a`} style={{ ...input, flex: 1, textAlign: "center", height: 44 }} value={fa.a} onChange={(e) => setFascia(di, fi, "a", e.target.value)} placeholder="15:00" />
                      </div>
                    ))}
                    <button data-testid={`fascia2-${d.giorno}`} onClick={() => toggleFascia2(di)} style={{ background: "none", border: "none", textAlign: "left", ...type.sansMedium, fontSize: 13, color: colors.accent, padding: 0 }}>{d.fasce.length > 1 ? "− Rimuovi seconda fascia" : "+ Seconda fascia"}</button>
                  </>
                ) : null}
              </div>
            ))}
          </Section>

          <Section title="Sessioni e ordini">
            <Field label="Scadenza automatica sessione (min)" value={String(f.scadenza_sessione_min)} onChange={(v) => setF({ ...f, scadenza_sessione_min: v })} testID="set-scadenza" />
            <Row label="Ordini attivi (kill switch)"><Switch testID="flag-ordini" value={!!f.flags?.ordini_attivi} onChange={(v) => setFlag("ordini_attivi", v)} /></Row>
            <Row label="Richiede conferma cameriere"><Switch testID="flag-conferma" value={!!f.flags?.richiede_conferma_cameriere} onChange={(v) => setFlag("richiede_conferma_cameriere", v)} /></Row>
          </Section>

          <div style={{ display: "flex", flexDirection: "column", gap: spacing.sm }}>
            <div style={{ ...type.sansSemi, fontSize: 13, color: colors.danger, textTransform: "uppercase", letterSpacing: 1 }}>Zona pericolosa</div>
            <div style={{ background: colors.dangerSoft, borderRadius: radius.lg, padding: spacing.md, border: `1px solid ${colors.danger}`, display: "flex", flexDirection: "column", gap: spacing.md }}>
              <span style={{ ...type.sans, fontSize: 13, color: colors.ink, lineHeight: "19px" }}>Elimina definitivamente il tuo account e tutti i dati del locale: menu, piatti, foto, tavoli e ordini. L'operazione non è reversibile.</span>
              <button data-testid="delete-account-button" onClick={() => { setDelPw(""); setDelErr(null); setDelOpen(true); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.sm, height: 48, borderRadius: radius.md, border: `1px solid ${colors.danger}`, background: colors.surface, ...type.sansSemi, fontSize: 15, color: colors.danger }}>
                <Trash2 size={18} color={colors.danger} />Elimina il mio account
              </button>
            </div>
          </div>
        </div>
      </div>

      {delOpen ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(26,24,21,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: spacing.lg, zIndex: 50 }}>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md, maxWidth: 420, width: "100%" }}>
            <div style={{ ...type.serif, fontSize: 22, color: colors.ink }}>Elimina account</div>
            <div style={{ ...type.sans, fontSize: 14, color: colors.inkSoft, lineHeight: "20px" }}>Questa azione cancella per sempre account, menu, tavoli e ordini. Per confermare, inserisci la tua password.</div>
            <input data-testid="delete-account-password" style={input} value={delPw} onChange={(e) => setDelPw(e.target.value)} placeholder="Password" type="password" autoCapitalize="none" />
            {delErr ? <div style={{ ...type.sansMedium, fontSize: 13, color: colors.danger }}>{delErr}</div> : null}
            <div style={{ display: "flex", gap: spacing.sm, marginTop: spacing.xs }}>
              <button data-testid="delete-account-cancel" onClick={() => setDelOpen(false)} disabled={delBusy} style={{ flex: 1, height: 48, borderRadius: radius.md, border: `1px solid ${colors.line}`, background: "transparent", ...type.sansSemi, fontSize: 15, color: colors.ink }}>Annulla</button>
              <button data-testid="delete-account-confirm" onClick={confirmDelete} disabled={delBusy || !delPw} style={{ flex: 1.4, height: 48, borderRadius: radius.md, border: "none", background: colors.danger, ...type.sansSemi, fontSize: 15, color: colors.onAccent, opacity: !delPw ? 0.4 : 1 }}>{delBusy ? "…" : "Elimina definitivamente"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing.sm }}>
      <div style={{ ...type.sansSemi, fontSize: 13, color: colors.inkSoft, textTransform: "uppercase", letterSpacing: 1 }}>{title}</div>
      <div style={{ background: colors.surface, borderRadius: radius.lg, padding: spacing.md, border: `1px solid ${colors.line}`, display: "flex", flexDirection: "column", gap: spacing.md }}>{children}</div>
    </div>
  );
}
function Field({ label, value, onChange, testID }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ ...type.sansMedium, fontSize: 14, color: colors.ink }}>{label}</label>
      <input data-testid={testID} style={input} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function Row({ label, children }) {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}><span style={{ ...type.sansMedium, fontSize: 14, color: colors.ink }}>{label}</span>{children}</div>;
}

const plain = { background: "none", border: "none", display: "flex", padding: 0 };
const input = { background: colors.bg, border: `1px solid ${colors.line}`, borderRadius: radius.md, height: 48, padding: `0 ${spacing.md}px`, ...type.sans, fontSize: 15, color: colors.ink };
const area = { background: colors.bg, border: `1px solid ${colors.line}`, borderRadius: radius.md, minHeight: 70, padding: spacing.md, ...type.sans, fontSize: 15, color: colors.ink, resize: "vertical" };
